import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mark } from '../components/Layout';
import { applyBrand } from '../lib/brand';
import { ErrorNote, LanguageSwitch } from '../components/ui';
import { get, post } from '../lib/api';
import { time } from '../lib/format';
import { useLoad } from '../lib/hooks';

interface Info {
  studentFirstName: string;
  studentFirstNameAr: string | null;
  schoolName: string;
  schoolNameAr: string | null;
  logoUrl: string | null;
  brandColor: string | null;
  status: string;
  departedAt: string | null;
  homeConfirmedAt: string | null;
  timezone: string;
}

/** Opened from the SMS link — no sign-in, one big button */
export function Confirm() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const { data, error, loading, setData } = useLoad(() => get<Info>(`/api/public/confirm/${token}`), [token]);
  useEffect(() => applyBrand(data?.brandColor), [data?.brandColor]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const ar = i18n.language === 'ar';

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await post<{ status: string; homeConfirmedAt: string }>(`/api/public/confirm/${token}`);
      setData((d) => (d ? { ...d, ...r } : d));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const name = data ? (ar && data.studentFirstNameAr) || data.studentFirstName : '';
  return (
    <div className="login">
      <div className="login-lang">
        <LanguageSwitch />
      </div>
      <section className="login-card confirm-card">
        <div className="login-brand">
          {data?.logoUrl ? <img className="login-logo" src={data.logoUrl} alt="" /> : <Mark />}
          <h1>{data ? (ar && data.schoolNameAr) || data.schoolName : t('app.name')}</h1>
        </div>
        {loading && <p>{t('common.loading')}</p>}
        {error && <p className="error-note">{t('confirm.invalid')}</p>}
        {data && data.status === 'home_confirmed' && (
          <div className="confirm-done">
            <div className="confirm-check" aria-hidden="true">✓</div>
            <h2>{t('confirm.done', { name })}</h2>
            <p>{t('confirm.doneAt', { time: time(data.homeConfirmedAt, data.timezone) })}</p>
          </div>
        )}
        {data && data.status === 'departed' && (
          <>
            <h2 className="confirm-q">{t('confirm.question', { name })}</h2>
            <p className="muted">{t('confirm.leftAt', { time: time(data.departedAt, data.timezone) })}</p>
            <ErrorNote error={err} />
            <button className="btn btn-signal btn-block btn-huge" onClick={confirm} disabled={busy}>
              {t('confirm.button')}
            </button>
          </>
        )}
        {data && !['departed', 'home_confirmed'].includes(data.status) && <p>{t('confirm.notApplicable')}</p>}
      </section>
    </div>
  );
}
